<template>
  <div class="ecolabels-content container-fluid p-4 pt-11 pb-12">
    <!-- Page Title -->
    <div class="d-flex flex-column align-items-center mb-5">
      <h2 class="mb-1">{{ t("ecolabels.upload.title") }}</h2>
      <p class="text-body-tertiary mb-0">
        {{ t("ecolabels.upload.subtitle") }}
      </p>
    </div>

    <div class="card">
      <div class="card-body">
        <!-- Header row -->
        <div
          class="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2"
        >
          <router-link to="/ecolabels" class="text-decoration-none small">
            <font-awesome-icon icon="arrow-left" class="me-1" />
            {{ t("ecolabels.upload.back_to_catalog") }}
          </router-link>
          <div v-if="sellerCompanies.length > 1" class="seller-select">
            <label class="form-label small mb-1">{{
              t("ecolabels.upload.seller_company")
            }}</label>
            <MultiSelectComponent
              v-model="selectedCompanyIds"
              :options="sellerCompanyOptions"
              :placeholder="t('ecolabels.upload.seller_company')"
              size="sm"
            />
          </div>
        </div>

        <!-- Submitted state -->
        <div
          v-if="submittedBatch"
          class="alert alert-success d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>{{
              t("ecolabels.upload.submitted_title", {
                count: submittedBatch.created_count,
              })
            }}</strong>
            <div
              v-if="submittedBatch.flagged_duplicate_pairs > 0"
              class="small"
            >
              {{
                t("ecolabels.upload.submitted_flagged", {
                  count: submittedBatch.flagged_duplicate_pairs,
                })
              }}
            </div>
          </div>
          <div class="d-flex gap-2">
            <router-link
              class="btn btn-outline-success btn-sm"
              to="/ecolabels?ecolabels-active-tab=submissions"
            >
              {{ t("ecolabels.upload.view_submissions") }}
            </router-link>
            <button class="btn btn-success btn-sm" @click="resetForNewUpload">
              {{ t("ecolabels.upload.upload_more") }}
            </button>
          </div>
        </div>

        <!-- AI chat on top, review grid full-width below: announcements and the chat
             already eat vertical space, so the grid gets the whole page width to show
             every column at once (EKOenergy feedback). -->
        <div class="row g-3">
          <!-- AI chat -->
          <div class="col-12">
            <AiChatCard
              v-if="selectedCompanyId"
              :key="selectedCompanyId"
              :companyId="selectedCompanyId"
              endpoint="/api/ecolabels/devices/chat"
              :title="t('ecolabels.upload.chat_title')"
              :greeting="t('ai_chat.greetings.ecolabel_devices')"
              :pendingHint="t('ecolabels.upload.pending_hint')"
              @analysis-complete="handleChatResult"
              @pending="chatPending = $event"
              @conversation="conversationId = $event"
            />
            <div v-else class="alert alert-warning">
              {{ t("ecolabels.upload.no_seller_company") }}
            </div>
          </div>

          <!-- Review grid -->
          <div class="col-12">
            <div class="card border-translucent h-100">
              <div
                class="card-header d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{{ t("ecolabels.upload.review_title") }}</strong>
                  <span v-if="rows.length" class="text-muted ms-2">
                    {{
                      t("ecolabels.upload.review_counts", {
                        total: rows.length,
                        selected: selectedRows.length,
                      })
                    }}
                  </span>
                </div>
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-outline-primary btn-sm"
                    @click="addManualRow"
                  >
                    <font-awesome-icon icon="plus" class="me-1" />
                    {{ t("ecolabels.upload.add_manually") }}
                  </button>
                  <button
                    v-if="rows.length && hasDuplicateRows"
                    class="btn btn-outline-secondary btn-sm"
                    @click="selectAllWithoutDuplicates"
                  >
                    {{ t("ecolabels.upload.select_all_without_duplicates") }}
                  </button>
                </div>
              </div>

              <div
                v-if="!rows.length"
                class="card-body text-center text-muted py-5"
              >
                <font-awesome-icon
                  icon="file-arrow-up"
                  size="2x"
                  class="mb-2"
                />
                <p class="mb-0">{{ t("ecolabels.upload.empty_state") }}</p>
              </div>

              <DataTable
                v-else
                ref="uploadTable"
                v-model:expandedRows="expandedRows"
                :value="rows"
                dataKey="_uid"
                :rowClass="rowClass"
                class="upload-review-table"
                :scrollable="rows.length > 15"
                :scrollHeight="rows.length > 15 ? '600px' : undefined"
              >
                <Column headerStyle="width: 2.5rem">
                  <template #body="{ data }">
                    <!-- Leading expander: the rest of the fields (address, coordinates,
                         link, ...) live in the expansion panel — one wrapping form. -->
                    <button
                      class="btn btn-link btn-sm p-0 text-body-secondary"
                      :title="t('ecolabels.upload.all_fields')"
                      :aria-expanded="!!expandedRows[data._uid]"
                      @click="toggleExpand(data)"
                    >
                      <font-awesome-icon
                        :icon="
                          expandedRows[data._uid]
                            ? 'chevron-down'
                            : 'chevron-right'
                        "
                      />
                    </button>
                  </template>
                </Column>

                <Column headerStyle="width: 2.5rem">
                  <template #header>
                    <!-- Master select-all lives in the header of the checkbox column
                         (standard table UX); indeterminate for partial selections. -->
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="allRowsSelected"
                      :indeterminate="anyRowSelected && !allRowsSelected"
                      :aria-label="t('ecolabels.upload.select_all')"
                      :title="
                        allRowsSelected
                          ? t('ecolabels.upload.deselect_all')
                          : t('ecolabels.upload.select_all')
                      "
                      @click.prevent="toggleSelectAll"
                    />
                  </template>
                  <template #body="{ data }">
                    <input
                      v-model="data.selected"
                      class="form-check-input"
                      type="checkbox"
                      :disabled="rowHasBlockingIssues(data)"
                      :title="
                        rowHasBlockingIssues(data)
                          ? t('ecolabels.upload.blocked_row_hint')
                          : ''
                      "
                    />
                  </template>
                </Column>

                <Column
                  :header="t('ecolabels.upload.flags')"
                  headerStyle="width: 7rem"
                >
                  <template #body="{ data }">
                    <div class="d-flex flex-wrap gap-1">
                      <span
                        v-if="rowHasBlockingIssues(data)"
                        class="badge bg-danger"
                        :title="missingFieldsTitle(data)"
                      >
                        {{ t("ecolabels.upload.flag_incomplete") }}
                      </span>
                      <button
                        v-if="data.duplicates.length"
                        class="badge bg-warning text-dark border-0"
                        :title="t('ecolabels.upload.flag_duplicate_hint')"
                        @click="toggleExpand(data)"
                      >
                        {{
                          t("ecolabels.upload.flag_duplicate", {
                            count: data.duplicates.length,
                          })
                        }}
                      </button>
                      <span
                        v-if="
                          ['SUSPECT', 'MULTIPLE'].includes(
                            data.validation.registration_id_quality
                          )
                        "
                        class="badge bg-secondary"
                        :title="t('ecolabels.upload.flag_reg_id_hint')"
                      >
                        {{
                          t("ecolabels.upload.flag_reg_id", {
                            quality: data.validation.registration_id_quality,
                          })
                        }}
                      </span>
                      <span
                        v-if="
                          !rowHasBlockingIssues(data) && !rowHasWarnings(data)
                        "
                        class="badge bg-success"
                      >
                        {{ t("ecolabels.upload.flag_ok") }}
                      </span>
                    </div>
                  </template>
                </Column>

                <Column
                  field="name"
                  :header="t('ecolabels.table.name')"
                  headerStyle="width: 13rem"
                >
                  <template #body="{ data }">
                    <InputText
                      v-model="data.name"
                      class="w-100 p-inputtext-sm"
                      :invalid="!data.name?.trim() && !hasIrecIdentity(data)"
                    />
                  </template>
                </Column>

                <Column
                  field="country"
                  :header="t('ecolabels.table.country')"
                  headerStyle="width: 11rem"
                >
                  <template #body="{ data }">
                    <!-- Searchable country picker; single-select via the one-element
                         array pattern (same as the seller company select above). -->
                    <div
                      :class="{
                        'country-select-invalid':
                          !data.country && !hasIrecIdentity(data),
                      }"
                    >
                      <MultiSelectComponent
                        :modelValue="data.country ? [data.country] : []"
                        :options="countryOptions"
                        :placeholder="t('ecolabels.table.country')"
                        size="sm"
                        portal
                        hideBulkActions
                        @update:modelValue="
                          (codes: string[]) =>
                            (data.country = codes[codes.length - 1] ?? null)
                        "
                      />
                    </div>
                  </template>
                </Column>

                <Column
                  field="renewable_type"
                  :header="t('ecolabels.table.renewable_type')"
                  headerStyle="width: 8rem"
                >
                  <template #body="{ data }">
                    <select
                      v-model="data.renewable_type"
                      class="form-select form-select-sm"
                      :class="{
                        'is-invalid':
                          !data.renewable_type && !hasIrecIdentity(data),
                      }"
                    >
                      <option :value="null">-</option>
                      <option
                        v-for="type in RENEWABLE_TYPES"
                        :key="type"
                        :value="type"
                      >
                        {{
                          t(`ecolabels.renewable_types.${type.toLowerCase()}`)
                        }}
                      </option>
                    </select>
                  </template>
                </Column>

                <Column
                  field="registration_authority"
                  :header="t('ecolabels.table.registration_authority')"
                  headerStyle="width: 7rem"
                >
                  <template #body="{ data }">
                    <!-- Required only when the row carries a registration ID -->
                    <select
                      v-model="data.registration_authority"
                      class="form-select form-select-sm"
                      @change="deriveIrecFields(data)"
                      :class="{
                        'is-invalid':
                          !!data.plant_ecolabel_id?.trim() &&
                          !data.registration_authority &&
                          !hasIrecIdentity(data),
                      }"
                    >
                      <option :value="null">-</option>
                      <option
                        v-for="authority in REGISTRATION_AUTHORITIES"
                        :key="authority"
                        :value="authority"
                      >
                        {{
                          REGISTRATION_AUTHORITY_LABELS[authority] ?? authority
                        }}
                      </option>
                    </select>
                  </template>
                </Column>

                <Column
                  field="plant_ecolabel_id"
                  :header="t('ecolabels.table.plant_ecolabel_id')"
                  headerStyle="width: 9rem"
                >
                  <template #body="{ data }">
                    <InputText
                      v-model="data.plant_ecolabel_id"
                      class="w-100 p-inputtext-sm"
                      @change="deriveIrecFields(data)"
                    />
                  </template>
                </Column>

                <Column headerStyle="width: 2.5rem">
                  <template #body="{ index }">
                    <button
                      class="btn btn-link btn-sm text-danger p-0"
                      :title="t('common.delete')"
                      @click="removeRow(index)"
                    >
                      <font-awesome-icon icon="times" />
                    </button>
                  </template>
                </Column>

                <template #expansion="{ data }">
                  <div class="device-expansion p-3 mb-2">
                    <DeviceFieldsPanel
                      :device="data"
                      :countryOptions="countryOptions"
                    />
                    <div
                      v-if="data.duplicates.length"
                      class="duplicate-section small mt-3 pt-3"
                    >
                      <div class="fw-semibold mb-1">
                        <font-awesome-icon
                          icon="triangle-exclamation"
                          class="text-warning me-2"
                        />
                        {{ t("ecolabels.upload.duplicate_details") }}
                      </div>
                      <div class="text-muted mb-2">
                        {{ t("ecolabels.upload.duplicate_what_to_do") }}
                      </div>
                      <div
                        v-for="(candidate, i) in data.duplicates"
                        :key="i"
                        class="d-flex align-items-baseline gap-2 py-1"
                      >
                        <span class="badge score-badge">
                          {{ Math.round(candidate.score * 100) }}%
                        </span>
                        <div>
                          <template v-if="candidate.device_id">
                            {{
                              t("ecolabels.upload.duplicate_of_catalog", {
                                name:
                                  candidate.name ?? `#${candidate.device_id}`,
                                status: candidate.status ?? "",
                              })
                            }}
                            <a
                              :href="catalogSearchUrl(candidate)"
                              target="_blank"
                              rel="noopener"
                              class="ms-1"
                            >
                              {{ t("ecolabels.upload.open_in_catalog") }}
                              <font-awesome-icon
                                icon="arrow-up-right-from-square"
                                class="ms-1"
                              />
                            </a>
                          </template>
                          <template v-else>
                            {{
                              t("ecolabels.upload.duplicate_of_row_named", {
                                row: duplicateRowNumber(candidate),
                                name: candidate.name ?? "?",
                              })
                            }}
                            <button
                              v-if="
                                candidate.draft_uid &&
                                rowIndexByUid(candidate.draft_uid) !== -1
                              "
                              class="btn btn-link btn-sm p-0 ms-1 align-baseline"
                              @click="jumpToRow(candidate.draft_uid)"
                            >
                              {{ t("ecolabels.upload.show_row") }}
                              <font-awesome-icon
                                icon="arrow-down"
                                class="ms-1"
                              />
                            </button>
                          </template>
                          <span class="text-muted">
                            ({{
                              candidate.reasons.map(reasonLabel).join(", ")
                            }})
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- Guided review: check (select) or delete the open row, then the
                         next unchecked row auto-expands — batches can be worked through
                         one by one without hunting for the next row. -->
                    <div class="d-flex justify-content-end gap-2 mt-3">
                      <button
                        class="btn btn-outline-danger btn-sm"
                        @click="deleteExpandedRow(data)"
                      >
                        <font-awesome-icon icon="times" class="me-1" />
                        {{ t("common.delete") }}
                      </button>
                      <button
                        class="btn btn-primary btn-sm"
                        :disabled="rowHasBlockingIssues(data)"
                        :title="
                          rowHasBlockingIssues(data)
                            ? t('ecolabels.upload.blocked_row_hint')
                            : ''
                        "
                        @click="checkRowAndAdvance(data)"
                      >
                        <font-awesome-icon icon="check" class="me-1" />
                        {{ t("ecolabels.upload.check_and_next") }}
                      </button>
                    </div>
                  </div>
                </template>
              </DataTable>

              <div
                v-if="rows.length"
                class="card-footer d-flex justify-content-end align-items-center gap-2"
              >
                <span v-if="blockedRowCount > 0" class="text-muted small">
                  {{
                    t("ecolabels.upload.blocked_rows_note", {
                      count: blockedRowCount,
                    })
                  }}
                </span>
                <button
                  class="btn btn-primary"
                  :disabled="
                    submitting || chatPending || selectedRows.length === 0
                  "
                  :title="chatPending ? t('ecolabels.upload.wait_for_ai') : ''"
                  @click="submitSelected"
                >
                  <span
                    v-if="submitting"
                    class="spinner-border spinner-border-sm me-2"
                  />
                  {{
                    t("ecolabels.upload.submit_selected", {
                      count: selectedRows.length,
                    })
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import {
  computed,
  onMounted,
  ref,
  useTemplateRef,
  watch,
  type ComponentPublicInstance,
} from "vue";
import { useI18n } from "vue-i18n";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import DeviceFieldsPanel from "./DeviceFieldsPanel.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ecolabelsApi } from "../api/ecolabelsApi";
import { isoCountryOptions, withUnknownCodes } from "../countryOptions";
import type {
  AuthorizedSellerCompany,
  DeviceChatResult,
  DeviceDuplicateCandidate,
  SubmitDevicesResponse,
} from "../api/ecolabelsTypes";
import {
  REGISTRATION_AUTHORITIES,
  REGISTRATION_AUTHORITY_LABELS,
  RENEWABLE_TYPES,
} from "../api/ecolabelsTypes";
import {
  applySelectAll,
  applySelectionWithoutDuplicates,
  areAllSelectableRowsSelected,
  createManualRow,
  mapChatResultToRows,
  mergeChatRows,
  missingRequiredFields,
  nextUncheckedRow,
  existingRowFingerprint,
  hasIrecIdentity,
  invalidNumberFields,
  NUMBER_FIELD_ERROR_KEYS,
  rowHasBlockingIssues,
  rowHasWarnings,
  toCreateRequest,
  type UploadRow,
} from "./deviceUploadMapping";
import { enrichIrecRow, enrichIrecRows } from "./irecLookup";

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const sellerCompanies = ref<AuthorizedSellerCompany[]>([]);
// MultiSelectComponent is the app's searchable dropdown; it models an array, so the seller
// selection is a one-element array kept single by the watcher below.
const selectedCompanyIds = ref<number[]>([]);
const rows = ref<UploadRow[]>([]);
const expandedRows = ref<Record<string, boolean>>({});
const uploadTable = useTemplateRef<ComponentPublicInstance>("uploadTable");
const highlightedUid = ref<string | null>(null);
const submitting = ref(false);
// True while a chat turn is in flight; submitting mid-turn could race the reconciliation.
const chatPending = ref(false);
const submittedBatch = ref<SubmitDevicesResponse | null>(null);
const conversationUploadIds = ref<number[]>([]);
const conversationId = ref<number | null>(null);
// AI rows the user deleted from the grid — later chat turns must not resurrect them.
// A count per ORIGINAL identity: mergeChatRows pairs fresh rows with survivors first
// and suppresses only unmatched leftovers, so these counts can't hit a kept twin.
const deletedFingerprintCounts = ref<Map<string, number>>(new Map());

const selectedCompanyId = computed(() => selectedCompanyIds.value[0] ?? null);

// Full ISO country list for the searchable per-row country picker; values are the
// alpha-2 codes the backend stores, labels the names people search by. Extracted rows
// can carry codes outside the list (regions like BE-FED, or the XX sentinel) — those get
// ad-hoc options so the picker still displays them instead of an empty placeholder.
const countryOptions = computed(() =>
  withUnknownCodes(
    isoCountryOptions(),
    rows.value.map((row) => row.country)
  )
);
const sellerCompanyOptions = computed(() =>
  sellerCompanies.value.map((company) => ({
    label: company.name,
    value: company.id,
  }))
);

const selectedRows = computed(() =>
  rows.value.filter((row) => row.selected && !rowHasBlockingIssues(row))
);
const blockedRowCount = computed(
  () => rows.value.filter(rowHasBlockingIssues).length
);
const allRowsSelected = computed(() =>
  areAllSelectableRowsSelected(rows.value)
);

const anyRowSelected = computed(() => rows.value.some((row) => row.selected));

const hasDuplicateRows = computed(() =>
  rows.value.some((row) => row.duplicates.length > 0)
);

watch(selectedCompanyIds, (ids, previous) => {
  if (ids.length > 1) {
    selectedCompanyIds.value = [ids[ids.length - 1]];
    return;
  }
  // Switching seller company starts a fresh submission context: the chat card remounts
  // (:key) so an in-flight turn can't merge into the new company's grid, and the grid,
  // provenance, and pending flag reset with it. Manual rows survive.
  if (previous.length && ids[0] !== previous[0]) {
    rows.value = rows.value.filter((row) => row.manual);
    conversationUploadIds.value = [];
    conversationId.value = null;
    deletedFingerprintCounts.value = new Map();
    chatPending.value = false;
    submittedBatch.value = null;
  }
});

onMounted(async () => {
  try {
    const response = await ecolabelsApi.listAuthorizedSellerCompanies();
    sellerCompanies.value = response.data.companies;
    if (sellerCompanies.value.length >= 1) {
      selectedCompanyIds.value = [sellerCompanies.value[0].id];
    }
  } catch (error) {
    globalErrorHandler(error);
  }
});

const handleChatResult = (data: unknown | null) => {
  if (data === null) {
    // "New chat" pressed — clear AI rows; keep manually entered ones.
    rows.value = rows.value.filter((row) => row.manual);
    conversationUploadIds.value = [];
    conversationId.value = null;
    deletedFingerprintCounts.value = new Map();
    return;
  }
  const result = data as DeviceChatResult;
  const manualRows = rows.value.filter((row) => row.manual);
  // Reconcile instead of replacing: user edits and deletions in the grid survive
  // follow-up chat turns (the chat re-emits the whole draft on every message).
  const aiRows = mergeChatRows(
    rows.value,
    mapChatResultToRows(result),
    deletedFingerprintCounts.value
  );
  rows.value = [...aiRows, ...manualRows];
  // Link-only / code-only extractions get their name, country, technology and location
  // from the public register — in the grid, where the seller reviews them.
  // NOTE: rows read back from the ref are reactive; filling the plain merged objects
  // writes the register's values without re-rendering the grid.
  void enrichIrecRows(rows.value.filter((row) => !row.manual));
  conversationUploadIds.value = Array.isArray(result.upload_ids)
    ? result.upload_ids
    : [];
  submittedBatch.value = null;
  // Clean AI rows arrive pre-checked, so this opens the first row needing attention.
  expandNextUnchecked();
};

const addManualRow = () => {
  const manualRow = createManualRow();
  rows.value = [manualRow, ...rows.value];
  // A fresh manual row is all blanks — open its full form right away.
  expandedRows.value = { [manualRow._uid]: true };
};

// "Select all" covers every selectable row, duplicate-flagged ones included.
const toggleSelectAll = () => {
  applySelectAll(rows.value, !allRowsSelected.value);
};

const selectAllWithoutDuplicates = () => {
  applySelectionWithoutDuplicates(rows.value);
};

/** Live I-REC shortcut from the grid's Registration ID / registry cells (after v-model
 * writes): derive the code/registry/link and fill the blanks from the public register. */
const deriveIrecFields = (row: UploadRow) => {
  void enrichIrecRow(row);
};

const toggleExpand = (row: UploadRow) => {
  expandedRows.value = expandedRows.value[row._uid] ? {} : { [row._uid]: true };
};

/** Guided review: open the next row that isn't checked yet (wrapping around), or
 * collapse everything once the whole batch is handled. Never scrolls — the user
 * controls the viewport. */
const expandNextUnchecked = (fromUid?: string) => {
  const next = nextUncheckedRow(rows.value, fromUid);
  expandedRows.value = next ? { [next._uid]: true } : {};
};

const checkRowAndAdvance = (row: UploadRow) => {
  row.selected = true;
  expandNextUnchecked(row._uid);
};

const deleteExpandedRow = (row: UploadRow) => {
  removeRow(rowIndexByUid(row._uid));
  expandNextUnchecked();
};

const removeRow = (index: number) => {
  const [removed] = rows.value.splice(index, 1);
  if (removed && !removed.manual) {
    const fingerprint = existingRowFingerprint(removed);
    deletedFingerprintCounts.value.set(
      fingerprint,
      (deletedFingerprintCounts.value.get(fingerprint) ?? 0) + 1
    );
  }
};

const rowIndexByUid = (uid: string): number =>
  rows.value.findIndex((row) => row._uid === uid);

/** Live 1-based position of a twin draft row. Rows shift as the grid is edited, so
 * resolve via uid like the "show row" jump does; the extraction-time index is only the
 * fallback when the target row no longer exists. */
const duplicateRowNumber = (candidate: DeviceDuplicateCandidate): number => {
  const index = candidate.draft_uid ? rowIndexByUid(candidate.draft_uid) : -1;
  return index !== -1 ? index + 1 : (candidate.draft_index ?? 0) + 1;
};

const rowClass = (row: UploadRow) =>
  row._uid === highlightedUid.value ? "upload-row-highlight" : "";

const scrollRowIntoView = (uid: string) => {
  const index = rowIndexByUid(uid);
  if (index === -1) return;
  // Rows render for real (no virtual scroller — it can't host the variable-height
  // expansion panels), so the row element itself can be scrolled into view.
  const tableEl = (uploadTable.value?.$el as HTMLElement | undefined) ?? null;
  const rowEl = tableEl?.querySelectorAll(
    "tbody > tr:not(.p-datatable-row-expansion)"
  )?.[index];
  rowEl?.scrollIntoView({ block: "center", behavior: "smooth" });
};

/** Scroll the grid to the suspected twin row and flash-highlight it. */
const jumpToRow = (uid: string) => {
  if (rowIndexByUid(uid) === -1) return;
  scrollRowIntoView(uid);
  highlightedUid.value = uid;
  window.setTimeout(() => {
    if (highlightedUid.value === uid) highlightedUid.value = null;
  }, 2500);
};

// "address_or_coordinates" has no form label of its own; everything else maps onto the
// add-device form's field labels.
const missingFieldLabel = (field: string) =>
  field === "address_or_coordinates"
    ? t("ecolabels.upload.field_address_or_coordinates")
    : t(`ecolabels.form.${field}`);

const missingFieldsTitle = (row: UploadRow) => {
  const missing = missingRequiredFields(row);
  const problems = invalidNumberFields(row).map((field) =>
    t(NUMBER_FIELD_ERROR_KEYS[field])
  );
  if (missing.length > 0) {
    problems.unshift(
      t("ecolabels.upload.missing_fields", {
        fields: missing.map(missingFieldLabel).join(", "),
      })
    );
  }
  return problems.join(" ");
};

/** Deep link into the catalog with the name filter prefilled, so the reviewer sees the
 * suspected duplicates in context. */
const catalogSearchUrl = (candidate: DeviceDuplicateCandidate): string => {
  const query = candidate.name ?? "";
  return `/ecolabels?device-name=${encodeURIComponent(query)}`;
};

const reasonLabel = (reason: string): string => {
  const key = reason.startsWith("name_fuzzy_") ? "name_fuzzy" : reason;
  const detail = reason.startsWith("name_fuzzy_")
    ? `${Math.round(Number(reason.replace("name_fuzzy_", "")) * 100)}%`
    : "";
  return t(`ecolabels.duplicates.reasons.${key}`, { detail });
};

const submitSelected = async () => {
  if (!selectedCompanyId.value || selectedRows.value.length === 0) return;
  submitting.value = true;
  try {
    const response = await ecolabelsApi.submitDevices({
      company_id: selectedCompanyId.value,
      devices: selectedRows.value.map(toCreateRequest),
      upload_ids: conversationUploadIds.value,
      conversation_id: conversationId.value,
    });
    submittedBatch.value = response.data;
    const submittedUids = new Set(selectedRows.value.map((row) => row._uid));
    rows.value = rows.value.filter((row) => !submittedUids.has(row._uid));
    globalSuccessHandler(
      t("ecolabels.upload.submitted_title", {
        count: response.data.created_count,
      }),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    submitting.value = false;
  }
};

const resetForNewUpload = () => {
  submittedBatch.value = null;
  rows.value = [];
  conversationUploadIds.value = [];
  conversationId.value = null;
  deletedFingerprintCounts.value = new Map();
};
</script>

<style scoped lang="scss">
.ecolabels-content {
  background-color: var(--phoenix-body-bg);
  border-radius: 1rem 1rem 0 0;
  overflow-y: auto;
  height: 100%;
  min-height: 0;
  overscroll-behavior: contain;
}

.seller-select {
  min-width: 280px;
}

// The all-fields editor inside a row expansion sits on the app canvas tone so it reads
// as a layer under the table, not another table row.
.device-expansion {
  background-color: var(--phoenix-body-bg);
  border-radius: 0.5rem;
}

// Possible duplicates: a quiet divided section with a warning accent, not a
// full-width orange alert shouting over the form.
.duplicate-section {
  border-top: 1px solid var(--bs-border-color);
}

.score-badge {
  background-color: var(--bs-warning-bg-subtle);
  color: var(--bs-warning-text-emphasis);
}

// Mirror PrimeVue's invalid-input treatment on the country picker wrapper.
.country-select-invalid :deep(button) {
  border-color: var(--bs-danger);
}

:deep(.upload-review-table) {
  .p-datatable-thead > tr > th {
    background-color: var(--bs-light);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    padding: 0.5rem;
  }

  .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem;
  }

  .p-inputtext-sm {
    font-size: 0.85rem;
    padding: 0.25rem 0.5rem;
  }

  .upload-row-highlight > td {
    background-color: var(--bs-warning-bg-subtle) !important;
    transition: background-color 0.3s ease;
  }
}
</style>
